import React, { useState, useEffect } from 'react'
import { useSearchParams, useParams, Link } from 'react-router-dom'
import { api } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  PRODUCT_CATEGORIES,
  CATEGORY_SLUG_TO_VALUE,
  CATEGORY_VALUE_TO_SLUG,
  CATEGORY_VALUE_TO_LABEL,
} from '../constants/product-categories'
import {
  MagnifyingGlassIcon,
  ShoppingBagIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'

interface SouvenirProduct {
  id: string
  name: string
  slug: string
  category: string
  description: string
  price: number | string
  compareAtPrice?: number | string
  images: string[]
  vendor: {
    id: string
    companyName: string
    city?: string
    state?: string
    isVerified: boolean
  }
  views: number
  totalSales: number
}

const SouvenirProductsPage: React.FC = () => {
  const { category: categorySlug } = useParams<{ category: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [products, setProducts] = useState<SouvenirProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Map URL category slug → backend value
  const categoryValue = categorySlug ? CATEGORY_SLUG_TO_VALUE[categorySlug] : undefined
  const searchParam = searchParams.get('search')

  useEffect(() => {
    fetchProducts()
  }, [categorySlug, searchParam])

  const fetchProducts = async () => {
    try {
      setLoading(true)
      let url = '/souvenir-products?isActive=true'

      if (categoryValue) {
        url += `&category=${categoryValue}`
      }

      if (searchParam) {
        url = `/souvenir-products/search/${searchParam}?isActive=true`
      }

      const response = await api.get(url)

      if (Array.isArray(response.data)) {
        setProducts(response.data)
      } else if (response.data && Array.isArray(response.data.data)) {
        setProducts(response.data.data)
      } else if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products)
      } else {
        console.error('Unexpected response format:', response.data)
        setProducts([])
      }
    } catch (err: any) {
      console.error('Error fetching products:', err)
      setError(err.response?.data?.message || 'Failed to fetch products')
      setProducts([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const searchTerm = formData.get('search') as string

    if (searchTerm) {
      setSearchParams({ search: searchTerm })
    } else {
      setSearchParams({})
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Islamic Souvenirs & Gifts</h1>
            <p className="text-gray-600 mt-1">Browse our collection of quality Islamic items</p>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="mb-4">
          <div className="relative">
            <input
              type="text"
              name="search"
              defaultValue={searchParam || ''}
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </form>

        {/* Category Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          <Link
            to="/products"
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              !categorySlug
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Products
          </Link>
          {PRODUCT_CATEGORIES.map((cat) => (
            <Link
              key={cat.value}
              to={`/products/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                categorySlug === cat.slug
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.label}
            </Link>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <ShoppingBagIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
          <p className="text-gray-500">
            {searchParam || categorySlug
              ? 'Try adjusting your search or filters'
              : 'Check back later for new products'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((product) => {
            const price = typeof product.price === 'number' ? product.price : parseFloat(product.price)
            const compareAtPrice = product.compareAtPrice
              ? (typeof product.compareAtPrice === 'number' ? product.compareAtPrice : parseFloat(product.compareAtPrice))
              : null
            const catSlug = CATEGORY_VALUE_TO_SLUG[product.category] || 'other'
            const detailPath = `/products/${catSlug}/${product.slug}`

            return (
              <div key={product.id} className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition">
                {/* Product Image */}
                <Link to={detailPath} className="block">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                      <ShoppingBagIcon className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                </Link>

                {/* Product Info */}
                <div className="p-4">
                  {/* Vendor */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-500">
                      {product.vendor.companyName}
                      {product.vendor.city && `, ${product.vendor.city}`}
                    </span>
                    {product.vendor.isVerified && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Verified
                      </span>
                    )}
                  </div>

                  {/* Name & Category */}
                  <Link to={detailPath}>
                    <h3 className="text-base font-semibold text-gray-900 hover:text-primary-600 transition line-clamp-2">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-xs text-gray-500 mt-1">{CATEGORY_VALUE_TO_LABEL[product.category] || product.category}</p>

                  {/* Price */}
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-900">
                      RM{price.toFixed(2)}
                    </span>
                    {compareAtPrice && compareAtPrice > price && (
                      <span className="text-sm text-gray-500 line-through">
                        RM{compareAtPrice.toFixed(2)}
                      </span>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                    <span>{product.totalSales} sold</span>
                    <span>{product.views} views</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default SouvenirProductsPage
